import { useEffect, useCallback } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { MapContainer } from "@boundary/components/mapContainer";
import { MapService } from "@domain/services/map.service";
import { MachineService } from "@domain/services/machine.service";
import { useMapStore, useMachineStore } from "@boundary/_hooks";

const MACHINE_ZOOM = 16;

export function MapLayout() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isVisible, hasInitialized } = useMapStore();
  const { items } = useMachineStore();
  
  const showMap = location.pathname === "/map";
  const machineIdFromUrl = searchParams.get("machineId");

  // Управление видимостью карты - синхронно при изменении пути
  // Используем useLayoutEffect для синхронного обновления перед рендером
  useEffect(() => {
    // Синхронно обновляем видимость сразу при изменении пути
    MapService.setVisibility(showMap);
  }, [showMap]);

  // Инициализация машин
  useEffect(() => {
    void MachineService.init();
  }, []);

  // Инициализация локации карты (только один раз при первом показе)
  // ВАЖНО: ждем загрузки аппаратов (items.length > 0)
  useEffect(() => {
    if (showMap && !hasInitialized && items.length > 0) {
      void MapService.initializeLocation();
    }
  }, [showMap, hasInitialized, items.length]);

  // Обработка machineId из URL для центрирования карты
  // Используем items.length вместо items, чтобы избежать лишних перерендеров
  useEffect(() => {
    if (showMap && machineIdFromUrl && items.length > 0) {
      const machineId = Number(machineIdFromUrl);
      const item = items.find((i) => i.id === machineId);
      if (item && item.latitude !== null && item.longitude !== null) {
        MachineService.selectMachine(machineId);
        MapService.setLocation({
          center: [item.longitude, item.latitude],
          zoom: MACHINE_ZOOM
        });
      }
    }
  }, [showMap, machineIdFromUrl, items.length]); // Используем items.length вместо items

  // Обработка клика на маркер
  const handleMarkerClick = useCallback((machineId: number) => {
    MachineService.selectMachine(machineId);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("machineId", machineId.toString());
    setSearchParams(newSearchParams, { replace: true });
    
    // Центрируем карту на аппарате
    const items = MachineService.getStore().getState().items;
    const item = items.find((i) => i.id === machineId);
    if (item && item.latitude !== null && item.longitude !== null) {
      MapService.setLocation({
        center: [item.longitude, item.latitude],
        zoom: MACHINE_ZOOM
      });
    }
  }, [searchParams, setSearchParams]);

  return <MapContainer onMarkerClick={handleMarkerClick} />;
}

